import {useAuth, useLogout} from "@hooks/auth.ts";
import {useCallback, useState} from "react";
import {deserializeValueNumbers, serializeValueNumbers} from "@types";

const serialize = <T>(data: T): string => {
    return JSON.stringify(serializeValueNumbers(data) as T);
}

const deserialize = <T>(data: any): T => {
    return deserializeValueNumbers(data) as T;
}

type GetAction = (url: string, abortController?: AbortController) => void
export const useGet = <T>(): [
        T | undefined,
    boolean,
        Error | undefined,
    GetAction
] => {
    const auth = useAuth();
    const [data, setData] = useState<T | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | undefined>(undefined);
    const [logout] = useLogout();

    const fn = useCallback(async (url: string, abortController?: AbortController) => {
        setLoading(true);
        setError(undefined);

        const controller = abortController ?? new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5_000);

        try {
            const response = await fetch(`${import.meta.env.VITE_BASE_URL}${url}`, {
                method: "GET",
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${auth.token}`
                },
                signal: controller.signal
            });

            if (response.status === 403) {
                logout();
                return;
            }

            if (!response.ok) {

                throw new Error(`Request failed: ${response.status} ${response.statusText}`);
            }

            const json = await response.json();
            setData(deserialize(json));
        } catch (err: any) {
            if (err.name === 'AbortError') {
                setError(new Error('Request timed out or aborted.'));
            } else {
                setError(err instanceof Error ? err : new Error('An unknown error occurred.'));
            }
        } finally {
            clearTimeout(timeoutId);
            setLoading(false);
        }
    }, [auth, logout]);

    return [data, loading, error, fn] as const;
};

type DeleteAction = (url: string, abortController?: AbortController) => void
export const useDelete = (): [
    boolean,
        Error | undefined,
    DeleteAction
] => {
    const auth = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | undefined>(undefined);
    const [logout] = useLogout();

    const fn = useCallback(async (url: string, abortController?: AbortController) => {
        setLoading(true);
        setError(undefined);

        const controller = abortController ?? new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5_000);

        try {
            const response = await fetch(`${import.meta.env.VITE_BASE_URL}${url}`, {
                method: "DELETE",
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${auth.token}`
                },
                signal: controller.signal
            });

            if (response.status === 403) {
                logout();
                return;
            }

            if (!response.ok) {
                throw new Error(`Request failed: ${response.status} ${response.statusText}`);
            }

        } catch (err: any) {
            if (err.name === 'AbortError') {
                setError(new Error('Request timed out or aborted.'));
            } else {
                setError(err instanceof Error ? err : new Error('An unknown error occurred.'));
            }
        } finally {
            clearTimeout(timeoutId);
            setLoading(false);
        }
    }, [auth, logout]);

    return [loading, error, fn] as const;
};

type PostAction = (url: string, data: object, abortController?: AbortController) => void
export const usePost = <T>(): [
        T | undefined,
    boolean,
        Error | undefined,
    PostAction,
] => {
    const auth = useAuth();
    const [data, setData] = useState<T | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | undefined>(undefined);
    const [logout] = useLogout();

    const fn = useCallback(async (url: string, body: object, abortController?: AbortController) => {
        setLoading(true);
        setError(undefined);

        const controller = abortController ?? new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5_000);

        try {
            const response = await fetch(`${import.meta.env.VITE_BASE_URL}${url}`, {
                method: "POST",
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${auth.token}`
                },
                body: serialize(body),
                signal: abortController?.signal
            });

            if (response.status === 403) {
                logout();
                return;
            }

            if (!response.ok) {

                throw new Error(`Request failed: ${response.status} ${response.statusText}`);
            }

            const json = await response.json();
            setData(deserialize(json));
        } catch (err: any) {
            if (err.name === 'AbortError') {
                setError(new Error('Request timed out or aborted.'));
            } else {
                setError(err instanceof Error ? err : new Error('An unknown error occurred.'));
            }
        } finally {
            clearTimeout(timeoutId);
            setLoading(false);
        }
    }, [auth, logout]);

    return [data, loading, error, fn];
}

type PatchAction = (url: string, data: object, abortController?: AbortController) => void
export const usePatch = <T>(): [
        T | undefined,
    boolean,
        Error | undefined,
    PatchAction
] => {
    const auth = useAuth();
    const [data, setData] = useState<T | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | undefined>(undefined);
    const [logout] = useLogout();

    const fn = useCallback(async (url: string, body: object, abortController?: AbortController) => {
        setLoading(true);
        setError(undefined);

        const controller = abortController ?? new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5_000);

        try {
            const response = await fetch(`${import.meta.env.VITE_BASE_URL}${url}`, {
                method: "PATCH",
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${auth.token}`
                },
                body: serialize(body),
                signal: abortController?.signal
            });

            if (response.status === 403) {
                logout();
                return;
            }

            if (!response.ok) {

                throw new Error(`Request failed: ${response.status} ${response.statusText}`);
            }

            const json = await response.json();
            setData(deserialize(json));
        } catch (err: any) {
            if (err.name === 'AbortError') {
                setError(new Error('Request timed out or aborted.'));
            } else {
                setError(err instanceof Error ? err : new Error('An unknown error occurred.'));
            }
        } finally {
            clearTimeout(timeoutId);
            setLoading(false);
        }
    }, [auth, logout]);

    return [data, loading, error, fn];
}


type PutAction = (url: string, data: object, abortController?: AbortController) => void
export const usePut = <T>(): [
        T | undefined,
    boolean,
        Error | undefined,
    PutAction
] => {
    const auth = useAuth();
    const [data, setData] = useState<T | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | undefined>(undefined);
    const [logout] = useLogout();

    const fn = useCallback(async (url: string, body: object, abortController?: AbortController) => {
        setLoading(true);
        setError(undefined);

        const controller = abortController ?? new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5_000);

        try {
            const response = await fetch(`${import.meta.env.VITE_BASE_URL}${url}`, {
                method: "PUT",
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${auth.token}`
                },
                body: serialize(body),
                signal: abortController?.signal
            });

            if (response.status === 403) {
                logout();
                return;
            }

            if (!response.ok) {

                throw new Error(`Request failed: ${response.status} ${response.statusText}`);
            }

            const json = await response.json();
            setData(deserialize(json));
        } catch (err: any) {
            if (err.name === 'AbortError') {
                setError(new Error('Request timed out or aborted.'));
            } else {
                setError(err instanceof Error ? err : new Error('An unknown error occurred.'));
            }
        } finally {
            clearTimeout(timeoutId);
            setLoading(false);
        }
    }, [auth, logout]);

    return [data, loading, error, fn];
}
